import { Button } from 'react-bootstrap';
import React, { useEffect, useRef, useState } from 'react';
import { toastError, toastWarning } from '../util/ToastifyUtil';
import { translateText } from '../api/translate';
import { useAuthData } from '../LoginContext';
import { unescapeHtmlEntities } from '../util/StringUtil';
import { useTranslationData } from '../TranslationContext';
import { handleApiError } from '../util/ErrorHandlerUtil';
import { useAuth0 } from '@auth0/auth0-react';
import { Gear } from 'react-bootstrap-icons';

const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID;

const TranslateButton = () => {
	const { auth0IdToken, rnResponseProjectId, consumerId, setRnResponseProjectId } = useAuthData();
	const { logout } = useAuth0();
	const { languageFrom, languageTo, textBefore, initialTextToTranslate, autoTranslate, isDetectLanguageLoading, setTextAfter, setAutoTranslate, setIsSettingsOpen } = useTranslationData();
	const [isFocused, setIsFocused] = useState<boolean>(false);

	const translateButtonRef = useRef<HTMLButtonElement>(null);
	const [loading, setLoading] = useState(false);
	const languageOptionDefault = 'Select a language';

	/**
	 * Keyboard event listener to automatically focus Translate Button if Tab key is pressed
	 */
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Tab') {
				if (document.activeElement !== translateButtonRef.current) {
					e.preventDefault();
					setIsFocused(true);
					translateButtonRef.current?.focus();
				}
			}
		};

		document.addEventListener('keydown', handleKeyDown);

		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [translateButtonRef]);

	/**
	 * SV-568 - Automatically perform text translation when a user selects the "Language I/O - Translate" option from the right-click context menu in the Chrome extension.
	 */
	useEffect(() => {
		// Run the translation process only when all the required parameters are populated and the autoTranslate flag is true.
		// This flag ensures that the user's automatic translation, is only triggered through the right-click context menu.
		const canRunAutoTranslate = autoTranslate && !isDetectLanguageLoading && initialTextToTranslate !== '' && textBefore !== '' && languageFrom !== '' && languageTo !== '' && consumerId !== null;

		if (canRunAutoTranslate) {
			onTranslate();
		}
	}, [languageFrom, autoTranslate, initialTextToTranslate, isDetectLanguageLoading]);

	const onTranslate = async () => {
		const hasSelectedFromLanguage = languageFrom && languageFrom !== languageOptionDefault;
		const hasSelectedToLanguage = languageTo && languageTo !== languageOptionDefault;
		const isValid =
			textBefore &&
			textBefore !== '' &&
			hasSelectedFromLanguage &&
			hasSelectedToLanguage;
		if (isValid) {
			setLoading(true);
			try {
				const translatedResponse = await translateText(
					textBefore,
					languageFrom,
					languageTo,
					rnResponseProjectId,
					auth0IdToken,
					consumerId
				);

				const unescapedHtmlEntitiesTranslatedText = unescapeHtmlEntities(translatedResponse.translatedText);
				setTextAfter(unescapedHtmlEntitiesTranslatedText);
				setRnResponseProjectId(translatedResponse.rnResponseProjectId);
			} catch (error: any) {
				const { message, shouldLogout } = handleApiError(error)
				if (shouldLogout) {
					localStorage.setItem('login-error-message', message);
					logout({
						clientId: clientId,
						logoutParams: {
							returnTo: window.location.origin
						}
					});
				} else {
					toastError(message);
					setTextAfter('');
				}

			} finally {
				setLoading(false);
				setIsFocused(false);
				setAutoTranslate(false)
			}
		} else {
			toastWarning('Please fill out the mandatory fields.');
		}
	};

	return (
		<div>
			<div className="d-flex gap-1 align-items-center justify-content-between">
					<span data-toggle="tooltip" title="Open Settings">
						<Gear
							type="button"
							className="mx-2 icon "
							onClick={() => setIsSettingsOpen(true)}
						/>
					</span>
				<Button
					ref={translateButtonRef}
					disabled={loading || isDetectLanguageLoading}
					onBlur={() => setIsFocused(false)}
					className={isFocused ? 'btn-brand-size focused-button' : 'btn-brand-size'}
					variant="brand"
					type="button"
					onClick={() => onTranslate()}
				>
					Translate
				</Button>
			</div>
		</div>
	);
};

export default TranslateButton;
